"use client";
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  type UniqueIdentifier,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  Box,
  Card,
  Checkbox,
  CircularProgress,
  Divider,
  Grid,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { filter, isEmpty, isNil, orderBy } from "lodash";
import Image from "next/image";
import React, { useEffect, useRef, useState } from "react";
import { BsTrash3Fill } from "react-icons/bs";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import type { cardListType, cardType, Translationtype } from "utils";
import { findEventTypeOption, generateIcon, getEventImageSrc } from "utils";
import theme from "../../../apps/dashboard/src/helpers/theme";
import { eventTypeOptions } from "../constants";
import { useReservationContext } from "../context";
import { updateCardsOrder } from "../services/questionCardService";
import { Confirm } from "./confirm";
import { toastMessage } from "./toast";
import { TransferListItem } from "./TransferListItem";
import TransferListSortableItem from "./TransferListSortableItem";

interface TransferListPops {
  loading?: boolean;
  admin?: boolean;
  editable?: boolean;
  hideable?: boolean;
  deletable?: boolean;
  defaultHidden?: boolean;
  checkboxSelection?: boolean;
  titles?: {
    leftList?: string;
    rightList?: string;
  };
  data: {
    leftList: {
      category: cardListType;
    };
    rightList?: cardType[];
  };
  newCard?: (cardData: cardType) => Promise<cardType | null>;
  updateCard?: (updatedCard: cardType) => void;
  deleteCard?: (id: string, isDefault?: boolean) => void;
  assignCardsToReservation?: (id: string) => void;
  unassignCardsFromReservation?: (id: string) => void;
  listItemClick?: (eventType: number) => void;
  t: Translationtype;
}

export function TransferList({
  loading = false,
  admin = false,
  editable = false,
  hideable = false,
  checkboxSelection = false,
  deletable = false,
  defaultHidden = false,
  // titles,
  data,
  newCard,
  updateCard,
  deleteCard,
  assignCardsToReservation,
  unassignCardsFromReservation,
  listItemClick,
  t,
}: TransferListPops) {
  const { reservationId } = useReservationContext();
  const [checked, setChecked] = useState<readonly cardType[]>([]);
  const [left, setLeft] = useState<readonly cardType[]>([]);
  const [cardValue, setCardValue] = useState<cardType>();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [hideList, setHideList] = useState(defaultHidden);
  const [existingCard, setExistingCard] = useState(false);
  const [selectedCard, setSelectedCard] = useState<cardType | null>(null);
  const handleDeleteModalOpen = () => setDeleteModalOpen(true);
  const handleDeleteModalClose = () => setDeleteModalOpen(false);
  const handleExistingCardModalOpen = () => setExistingCard(true);
  const handleExistingCardModalClose = () => setExistingCard(false);
  // const [right, setRight] = useState<readonly cardType[]>([]);
  const addCardInput = useRef<HTMLInputElement>(null);
  const [activeItem, setActiveItem] = useState<cardType | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (data) {
      const orderedList = deletable
        ? data.leftList.category.data
        : orderBy(
            data.leftList.category.data,
            ["isDefault", "createdAt"],
            ["asc", "desc"]
          );
      setLeft(orderedList);
    } else {
      setLeft([]);
    }
  }, [data, deletable]);

  useEffect(() => {
    const delayInputTimeoutId = setTimeout(() => {
      if (updateCard && !isNil(cardValue)) {
        updateCard(cardValue);
        setCardValue(undefined);
      }
    }, 500);
    return () => clearTimeout(delayInputTimeoutId);
  }, [updateCard, cardValue]);

  async function updateVideoCardsOrder(
    newOrder: cardType[],
    oldOrder: cardType[]
  ) {
    try {
      const result = await updateCardsOrder({
        reservationId,
        questionCardIds: newOrder.map((card) => card.id),
      });
      if (!result.data.success) {
        toastMessage.error(t("Toast.errorCardsOrder"));
        toastMessage.validationError(result.data.validationErrors);

        setLeft(() => {
          return oldOrder;
        });
      }
      setActiveItem(null);
    } catch (error) {
      toastMessage.error(t("Toast.errorCardsOrder"));
      console.error(error);
      setLeft(() => {
        return oldOrder;
      });
      setActiveItem(null);
    }
  }

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    card: cardType
  ) => {
    setCardValue({ ...card, questionText: event.target.value });
  };

  const handleToggle = (value: cardType) => () => {
    const current = checked.filter((chk) => chk.id.includes(value.id));
    if (value.selected && unassignCardsFromReservation) {
      unassignCardsFromReservation(value.id);
    }
    if (isNil(value.selected) && assignCardsToReservation) {
      assignCardsToReservation(value.id);
    }
    const newChecked = [...checked];

    if (isEmpty(current)) {
      newChecked.push(value);
    } else {
      const currentIndex = newChecked.findIndex((nchk) => nchk.id === value.id);
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const createNewCard = async (questionText: string, isDefault: boolean) => {
    const cardObject: cardType = {
      id: `${left.length}`,
      questionText,
      eventType: data.leftList.category.eventType,
      languageCode: data.leftList.category.languageCode,
      reservationId: null,
    };
    if (newCard) {
      const createdCard = await newCard(cardObject);
      if (!isNil(createdCard?.id)) {
        cardObject.id = createdCard?.id;
        cardObject.reservationId = createdCard?.reservationId;
      }
    }
    const newList = [{ ...cardObject, isDefault }, ...left];
    setLeft(newList);
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const currentActiveItem = left.find((item) => item.id === active.id);
    if (currentActiveItem) {
      setActiveItem(currentActiveItem);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    performDrag(active.id, over.id);
  };

  const performDrag = (
    activeId: UniqueIdentifier,
    overId: UniqueIdentifier
  ) => {
    if (activeId !== overId) {
      setLeft((items) => {
        const oldIndex = items.findIndex((item) => item.id === activeId);
        const newIndex = items.findIndex((item) => item.id === overId);
        const newOrder = arrayMove([...items], oldIndex, newIndex);
        updateVideoCardsOrder(newOrder, [...items]);
        return newOrder;
      });
    }
  };

  const handleUnassignCard = (cardId: string) => {
    setSelectedCard(left.find((card) => card.id === cardId));
    handleDeleteModalOpen();
  };

  const customList = (title: string, items: readonly cardType[]) => (
    <Grid item width={{ xs: "100%", sm: "100%", md: "100%" }}>
      <Card>
        {hideable ? (
          <div className="flex flex-row justify-between">
            <div className="ml-4">
              <p className="font-bold">
                {`${left.length} ${t("QuestionCard.total")}`}
              </p>
            </div>
            <div className="mr-4">
              <span
                onClick={() => setHideList(!hideList)}
                className="block"
                title={t("Global.edit")}
              >
                {hideList
                  ? generateIcon(FaChevronDown, "text-[22px] cursor-pointer")
                  : generateIcon(FaChevronUp, "text-[22px] cursor-pointer")}
              </span>
            </div>
          </div>
        ) : null}
        <Divider />
        {hideable && hideList ? null : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <List
              sx={{
                bgcolor: "background.paper",
                overflow: "auto",
              }}
              dense
              component="div"
              role="list"
            >
              <SortableContext
                items={[...items]}
                strategy={verticalListSortingStrategy}
              >
                {items.map((value: cardType, index) => {
                  const labelId = `transfer-list-all-item-${value.id}-label`;

                  return deletable ? (
                    <TransferListSortableItem
                      key={value.id}
                      item={value}
                      t={t}
                      deleteCard={handleUnassignCard}
                      listItemClick={listItemClick}
                    />
                  ) : (
                    <>
                      {index !== 0 ? <Divider /> : null}
                      <ListItemButton
                        key={value.id}
                        role="listitem"
                        disableRipple
                        onClick={handleToggle(value)}
                      >
                        {checkboxSelection && value.isDefault ? (
                          <ListItemIcon>
                            <Checkbox
                              defaultChecked={value.selected}
                              tabIndex={-1}
                              disableRipple
                              inputProps={{
                                "aria-labelledby": labelId,
                              }}
                            />
                          </ListItemIcon>
                        ) : null}
                        {admin || (editable && !value.isDefault) ? (
                          <input
                            className="input input-bordered w-full focus:outline-hoverColor"
                            id="cardEdit"
                            defaultValue={value.questionText}
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>
                            ) => handleInputChange(event, value)}
                          />
                        ) : (
                          <ListItemText
                            id={labelId}
                            primary={value.questionText}
                          />
                        )}
                        {admin ||
                        (editable && !value.isDefault) ||
                        deletable ? (
                          <div>
                            <div className="flex flex-col md:flex-row items-center gap-2">
                              {deletable ? (
                                <div className="flex flex-col items-center">
                                  <h2 className="text-left font-semibold">
                                    {value.isDefault
                                      ? t(
                                          `Options.event.${findEventTypeOption(
                                            value.eventType,
                                            eventTypeOptions
                                          )}` as any
                                        )
                                      : t("Options.event.CustomQuestions")}
                                  </h2>
                                </div>
                              ) : null}
                              {deletable && value.isDefault ? (
                                <Image
                                  src={getEventImageSrc(value.eventType)}
                                  priority
                                  width={40}
                                  height={40}
                                  alt="LOGO"
                                />
                              ) : null}
                              <div className="px-2">
                                <BsTrash3Fill
                                  className="w-[30px] h-[30px] text-error ml-2 cursor-pointer"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    const itemIndex = items.findIndex(
                                      (i) =>
                                        i.questionText === value.questionText
                                    );
                                    setSelectedCard(items[itemIndex]);
                                    handleDeleteModalOpen();
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </ListItemButton>
                    </>
                  );
                })}
              </SortableContext>
              <DragOverlay>
                {activeItem ? (
                  <TransferListItem
                    item={activeItem}
                    t={t}
                    loading
                    deleteCard={handleUnassignCard}
                    listItemClick={listItemClick}
                  />
                ) : null}
              </DragOverlay>
            </List>
          </DndContext>
        )}
      </Card>
    </Grid>
  );

  return loading ? (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        "& .MuiCircularProgress-colorPrimary": {
          color: theme.colors.purple[950],
        },
      }}
    >
      <CircularProgress />
    </Box>
  ) : (
    <Box sx={{ width: "100%", height: "100%" }}>
      {editable ? (
        <div className="flex flex-row mb-1">
          <input
            className="input input-bordered w-full focus:outline-hoverColor"
            id="newCardInput"
            defaultValue=""
            ref={addCardInput}
          />
          <button
            type="button"
            className="bg-gray-700 text-white font-bold rounded ml-1 w-64"
            onClick={async () => {
              if (addCardInput.current && addCardInput.current.value !== "") {
                const checkForExistingCard = filter(left, [
                  "questionText",
                  addCardInput.current.value,
                ]);
                if (!isEmpty(checkForExistingCard)) {
                  handleExistingCardModalOpen();
                } else {
                  await createNewCard(addCardInput.current.value, admin);
                  addCardInput.current.value = "";
                }
              }
            }}
          >
            {t("QuestionCard.addNewCard")}
          </button>
        </div>
      ) : null}
      <Grid
        container
        // spacing={2}
        justifyContent="center"
        alignItems="center"
        wrap="nowrap"
        direction={{ xs: "column", sm: "row", md: "row" }}
      >
        {!isEmpty(left) &&
          customList(data.leftList.category.eventTypeTitle, left)}
      </Grid>
      <Confirm
        handleOk={() => {
          const index = left.findIndex(
            (i) => i.questionText === selectedCard?.questionText
          );
          const updatedLeft = [...left];
          updatedLeft.splice(index, 1);
          if (deleteCard && selectedCard) {
            deleteCard(selectedCard?.id, selectedCard?.isDefault);
          }
          setLeft(updatedLeft);
          handleDeleteModalClose();
        }}
        handleClose={handleDeleteModalClose}
        isOpened={deleteModalOpen}
        okBtnText={t("Buttons.delete")}
        okBtnClassName="btn btn-error btn-outline"
        cancelBtnClassName="btn btn-icon"
        title={t("QuestionCard.deleteConfirmTitle")}
        t={t}
      >
        {selectedCard?.questionText}
      </Confirm>
      <Confirm
        handleOk={handleExistingCardModalClose}
        handleClose={handleExistingCardModalClose}
        isOpened={existingCard}
        okBtnText={t("Buttons.ok")}
        okBtnClassName="btn btn-icon"
        title={t("QuestionCard.existingCardConfirmTitle")}
        t={t}
      />
    </Box>
  );
}
